/**
 * /admin/paywall-audit — one-screen visibility into every paywall gate
 * and per-plan subscriber counts. Built after Janis's "double-charged
 * for Storefront" bug to make policy drift discoverable at a glance.
 */
import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldCheckIcon as ShieldCheck, WarningIcon as Warning, UsersIcon as Users, ArrowsClockwiseIcon as ArrowsClockwise, LockIcon as Lock, LockOpenIcon as LockOpen } from "@phosphor-icons/react";

export default function AdminPaywallAudit() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [overlapDialog, setOverlapDialog] = useState(false);
  const [overlapData, setOverlapData] = useState(null);
  const [overlapLoading, setOverlapLoading] = useState(false);

  const openOverlapDrill = async () => {
    setOverlapDialog(true);
    if (overlapData) return;
    setOverlapLoading(true);
    try {
      const r = await api.get("/admin/paywall-audit/overlapping-users");
      setOverlapData(r.data);
    } catch (e) {
      setOverlapData({ users: [], error: e?.response?.data?.detail || String(e) });
    } finally {
      setOverlapLoading(false);
    }
  };

  const refundOverlap = async (userId, subscriptionId, planLabel) => {
    if (!window.confirm(`Cancel the "${planLabel}" subscription and refund the prorated remainder via Stripe?\n\nThe user's other active plans are not affected. The action is logged to admin_audit_log.`)) {
      return;
    }
    try {
      const r = await api.post("/admin/paywall-audit/refund-overlap", {
        user_id: userId,
        subscription_id: subscriptionId,
      });
      const refund = r.data?.refund_calc?.refund_amount;
      const stripe = r.data?.stripe_result || {};
      const emailed = r.data?.customer_email_sent;
      if (stripe.ok) {
        toast.success(
          `Refunded $${refund?.toFixed(2)} via Stripe (id: ${stripe.refund_id})` +
            (emailed ? " · customer notified by email" : "")
        );
      } else if (stripe.skipped) {
        toast.success("Subscription cancelled. No Stripe charge linked — refund manually if needed.");
      } else {
        toast.error(`Sub cancelled but Stripe refund failed: ${stripe.error}`);
      }
      // Reload the dialog data so the cancelled sub disappears
      setOverlapData(null);
      const fresh = await api.get("/admin/paywall-audit/overlapping-users");
      setOverlapData(fresh.data);
      // Also bust the parent audit totals
      const audit = await api.get("/admin/paywall-audit");
      setData(audit.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refund failed");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/paywall-audit");
      setData(r.data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load audit");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/admin/paywall-audit");
        if (!cancelled) {
          setData(r.data);
          setErr(null);
        }
      } catch (e) {
        if (!cancelled) {
          setErr(e?.response?.data?.detail || "Failed to load audit");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 py-6">
        <header className="mb-8 flex items-start justify-between gap-4 flex-wrap" data-testid="paywall-audit-header">
          <div>
            <h1 className="font-spectral text-4xl tracking-tight flex items-center gap-3">
              <ShieldCheck size={32} weight="duotone" className="text-[#C9A227]" />
              Paywall audit
            </h1>
            <p className="text-sm text-stone-600 mt-1">
              Every gate, every plan, every drift signal — on one page.
            </p>
          </div>
          <button
            onClick={load}
            className="text-sm text-stone-700 underline hover:text-stone-900 flex items-center gap-1"
            data-testid="paywall-audit-refresh"
          >
            <ArrowsClockwise size={14} /> Refresh
          </button>
        </header>

        {err && (
          <div className="rounded-xl bg-red-50 border border-red-200 p-5 text-sm text-red-800" data-testid="paywall-audit-error">
            {String(err)}
          </div>
        )}

        {loading && !data && (
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-96 w-full" />
          </div>
        )}

        {data && (
          <>
            {/* Totals strip */}
            <section className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-testid="paywall-audit-totals">
              <Stat
                icon={Users}
                label="Total paying users"
                value={data.totals.paying_users}
                accent="#0E0E0E"
              />
              <Stat
                icon={Warning}
                label="Users with overlapping subs"
                value={data.totals.users_with_overlapping_subs}
                accent={data.totals.users_with_overlapping_subs > 0 ? "#C1272D" : "#10B981"}
                hint={
                  data.totals.users_with_overlapping_subs > 0
                    ? "Click to see who — flagged double-charges sort to the top."
                    : "Clean — no double-billed users."
                }
                onClick={data.totals.users_with_overlapping_subs > 0 ? openOverlapDrill : null}
                testid="stat-overlapping-subs"
              />
              <Stat
                icon={ShieldCheck}
                label="Gates audited"
                value={data.gates.length}
                accent="#C9A227"
              />
            </section>

            {/* Plans table */}
            <section className="mb-10" data-testid="paywall-audit-plans">
              <h2 className="font-spectral text-2xl tracking-tight mb-4">
                Active plans
              </h2>
              <div className="rounded-2xl border border-[#E5E5E5] overflow-hidden bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 text-stone-500 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-left py-3 px-4 font-medium">Plan</th>
                      <th className="text-right py-3 px-4 font-medium">Active</th>
                      <th className="text-right py-3 px-4 font-medium">Expiring in 30 days</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono tabular-nums">
                    {data.plans.length === 0 && (
                      <tr>
                        <td colSpan="3" className="text-center py-8 text-stone-500">
                          No active subscriptions in the database.
                        </td>
                      </tr>
                    )}
                    {data.plans.map((p) => (
                      <tr
                        key={p.slug}
                        className="border-t border-stone-100"
                        data-testid={`paywall-audit-plan-${p.slug}`}
                      >
                        <td className="py-3 px-4 font-sans font-medium">{p.slug}</td>
                        <td className="py-3 px-4 text-right">{p.active}</td>
                        <td
                          className={`py-3 px-4 text-right ${
                            p.expiring_30d > 0 ? "text-amber-700 font-semibold" : "text-stone-400"
                          }`}
                        >
                          {p.expiring_30d || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Gates list */}
            <section data-testid="paywall-audit-gates">
              <h2 className="font-spectral text-2xl tracking-tight mb-2">
                Gate catalogue
              </h2>
              <p className="text-sm text-stone-600 mb-6">
                Each gate&apos;s source-of-truth function, the plans it
                accepts, and how many users currently pass it. Update{" "}
                <code className="bg-stone-100 px-1 rounded">GATE_CATALOGUE</code>{" "}
                in <code className="bg-stone-100 px-1 rounded">/app/backend/routers/admin_paywall_audit.py</code>{" "}
                whenever a new gate or plan is added.
              </p>
              <div className="space-y-4">
                {data.gates.map((g) => (
                  <GateCard key={g.name} gate={g} />
                ))}
              </div>
            </section>
          </>
        )}

        {/* Overlap drill-down dialog */}
        <Dialog open={overlapDialog} onOpenChange={setOverlapDialog}>
          <DialogContent
            className="max-w-3xl max-h-[85vh] overflow-y-auto"
            data-testid="overlap-dialog"
          >
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Warning size={20} weight="duotone" className="text-[#C1272D]" />
                Users with overlapping subscriptions
              </DialogTitle>
            </DialogHeader>
            {overlapLoading && (
              <div className="space-y-3 py-4">
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
              </div>
            )}
            {!overlapLoading && overlapData && (
              <div className="space-y-3 mt-2">
                {overlapData.error && (
                  <p className="text-sm text-red-700">{overlapData.error}</p>
                )}
                {overlapData.users?.length === 0 && (
                  <p className="text-sm text-stone-600">No overlapping users found.</p>
                )}
                {overlapData.users?.map((u) => (
                  <article
                    key={u.user_id}
                    className={`rounded-xl border p-4 ${
                      u.double_pay_flags.length > 0
                        ? "border-red-300 bg-red-50"
                        : "border-stone-200 bg-stone-50"
                    }`}
                    data-testid={`overlap-row-${u.user_id}`}
                  >
                    <header className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm text-stone-900 truncate">
                          {u.name || u.email || u.user_id}
                        </p>
                        {u.email && u.name && (
                          <p className="text-xs text-stone-500 font-mono truncate">
                            {u.email}
                          </p>
                        )}
                        <p className="text-[10px] text-stone-400 font-mono mt-1">
                          {u.user_id}
                        </p>
                      </div>
                      {u.double_pay_flags.length > 0 ? (
                        <Badge className="bg-[#C1272D] text-white text-[10px] uppercase tracking-wider font-mono">
                          Flagged double-pay
                        </Badge>
                      ) : (
                        <Badge className="bg-stone-200 text-stone-700 text-[10px] uppercase tracking-wider font-mono">
                          Distinct plans
                        </Badge>
                      )}
                    </header>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {u.subscriptions.map((s, i) => (
                        <div
                          key={i}
                          className="inline-flex items-center gap-1 bg-white border border-stone-300 rounded px-2 py-1"
                        >
                          <span className="font-mono text-[11px] text-stone-800">
                            {s.plan} · ends {s.expires_at?.slice(0, 10)}
                          </span>
                          <button
                            type="button"
                            onClick={() => refundOverlap(u.user_id, s.id, s.plan)}
                            className="text-[10px] uppercase tracking-wider text-[#C1272D] hover:text-red-900 font-semibold ml-1"
                            data-testid={`refund-overlap-${u.user_id}-${s.id}`}
                          >
                            Refund
                          </button>
                        </div>
                      ))}
                    </div>
                    {u.double_pay_flags.length > 0 && (
                      <ul className="text-xs text-red-800 space-y-0.5 list-disc list-outside pl-5">
                        {u.double_pay_flags.map((f, i) => (
                          <li key={i} className="font-mono">{f}</li>
                        ))}
                      </ul>
                    )}
                  </article>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AppShell>
  );
}

function Stat({ icon: Icon, label, value, accent, hint, onClick, testid }) {
  const interactive = !!onClick;
  return (
    <div
      onClick={onClick}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      className={`rounded-2xl border border-[#E5E5E5] bg-white p-5 ${
        interactive ? "cursor-pointer hover:border-stone-400 hover:shadow-sm transition" : ""
      }`}
      data-testid={testid || `stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs uppercase tracking-wider text-stone-500 font-medium">
          {label}
        </p>
        <Icon size={20} weight="duotone" style={{ color: accent }} />
      </div>
      <p
        className="font-spectral text-4xl font-light tabular-nums"
        style={{ color: accent }}
      >
        {value}
      </p>
      {hint && <p className="text-xs text-stone-500 mt-2 leading-relaxed">{hint}</p>}
    </div>
  );
}

function GateCard({ gate }) {
  return (
    <article
      className="rounded-2xl border border-[#E5E5E5] bg-white p-6"
      data-testid={`paywall-audit-gate-${gate.name}`}
    >
      <header className="flex items-start justify-between gap-4 mb-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="font-mono text-base font-semibold text-stone-900">
              {gate.name}
            </h3>
            {gate.admin_override ? (
              <Badge className="bg-stone-100 text-stone-700 text-[10px] uppercase tracking-wider font-mono">
                <LockOpen size={10} className="mr-1" /> admin override
              </Badge>
            ) : (
              <Badge className="bg-red-50 text-red-700 text-[10px] uppercase tracking-wider font-mono border border-red-200">
                <Lock size={10} className="mr-1" /> strict
              </Badge>
            )}
          </div>
          <p className="text-xs text-stone-500 font-mono">{gate.file}</p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-xs uppercase tracking-wider text-stone-500 mb-0.5">
            Users with access
          </p>
          <p className="font-spectral text-3xl font-light tabular-nums text-[#C9A227]">
            {gate.users_with_access}
          </p>
        </div>
      </header>
      <p className="text-sm text-stone-800 mb-3 leading-relaxed">
        <span className="font-semibold">Unlocks: </span>
        {gate.unlocks}
      </p>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {gate.plans_granting?.length ? (
          gate.plans_granting.map((p) => (
            <Badge
              key={p}
              className="bg-[#C9A22715] text-[#8A6A00] border border-[#C9A22740] font-mono text-[11px]"
            >
              {p}
            </Badge>
          ))
        ) : (
          <span className="text-xs text-stone-500 italic">No plans grant this gate.</span>
        )}
      </div>
      <p className="text-xs text-stone-600 leading-relaxed">
        <span className="font-semibold text-stone-700">Bundling: </span>
        {gate.bundling}
      </p>
      {gate.caveats && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3 leading-relaxed">
          <span className="font-semibold">Note: </span>
          {gate.caveats}
        </p>
      )}
    </article>
  );
}
